<template>
    <div>
        <BackButton />
        <ProgressLoader v-if="loading" />
        <div v-if="!loading && !courseExists" class="course-error">
            <v-img
                :src="require('@/assets/search-error.gif')"
                alt="Course does not exist"
                max-width="400px"
                min-height="400px"
            />
            <div class="tw-italic">
                {{ $t('message.courseDoesNotExist') }}
            </div>
        </div>
        <div v-if="!loading && courseExists">
            <CourseIntroduction :courseObject="courseItem" />

            <!-- Book Course Section -->
            <hr class="tw-my-6" />

            <v-container class="tw-flex tw-flex-col tw-gap-4 tw-px-6">
                <h1 class="tw-text-2xl tw-font-bold">
                    {{ $t('message.bookThisCourse') }}
                </h1>
                <!-- null spotsAvailable means unlimited -->
                <v-chip
                    v-if="spotsAvailable !== null"
                    :color="spotsAvailable > 0 ? 'success' : 'disabled'"
                    class="tw-w-fit"
                >
                    {{ spotsAvailable }} {{ $t('message.spotsAvailable') }}
                </v-chip>

                <p v-if="!isUserAuth" class="tw-italic tw-text-red-500 !tw-m-0">
                    {{ $t('message.loginToPurchase') }}
                </p>
                <div v-else>
                    <SelectUsers
                        :userId="user.id"
                        :studioId="studio.id"
                        :value="bookingType"
                        :registeredSubAccounts="registeredUsers"
                        :availableBookingTypes="
                            courseItem.isFamilyBookingEnabled
                                ? ['myself', 'family']
                                : ['myself']
                        "
                        :maxFamilyBooking="spotsAvailable"
                        @input="onChangeBookingType"
                        @updateSelectedSubAccounts="updateSelectedSubAccounts"
                    />
                </div>

                <div
                    class="tw-mt-2 tw-flex tw-flex-row tw-items-center tw-justify-between tw-rounded-lg tw-bg-white tw-p-4 night:tw-bg-card"
                >
                    <div class="tw-flex tw-flex-col">
                        <span class="tw-text-sm tw-font-bold">
                            {{ $t('message.totalPurchase') }}
                        </span>
                        <span class="tw-text-sm tw-font-normal">
                            {{ countBooking }}
                            {{ $t('message.persons') }}
                            x
                            {{ courseItem.price }}
                            {{ this.studio.currency }}
                        </span>
                    </div>
                    <span class="tw-text-2xl tw-font-bold">
                        {{ countTotalPrice }}
                        {{ this.studio.currency }}
                    </span>
                </div>

                <span
                    v-if="hasMainUserRegistered && !isFamilyBooking"
                    class="tw-italic tw-text-gray-500"
                >
                    {{ $t('message.courseAlreadyRegistered') }}
                </span>

                <span v-if="!isBookable" class="tw-italic tw-text-gray-500">
                    {{ reason }}
                </span>

                <v-btn
                    class="primary !tw-h-12 tw-w-full tw-rounded-lg tw-bg-primary tw-text-white"
                    :disabled="
                        (isFamilyBooking && selectedSubAccounts.length === 0) ||
                        (!isFamilyBooking && hasMainUserRegistered) ||
                        !isBookable
                    "
                    @click="submit"
                >
                    {{ $t('message.purchaseAndBookCourse') }}
                </v-btn>
            </v-container>

            <CheckoutWindow
                ref="CheckoutWindow"
                :courseItem="courseItem"
                :isPurchaseCourse="true"
                :userIds="
                    isFamilyBooking
                        ? selectedSubAccounts.map((subAccount) => subAccount.id)
                        : null
                "
                :hideButton="true"
                :key="checkoutWindowKey"
                :refreshAfterSuccess="true"
                @closeDialog="reload"
                @error="handleError"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getCourseByCourseId } from '@/util/queries/coursesQueries'
import {
    AUTHENTICATION_MODULE_NAME,
    STUDIO_MODULE_NAME,
} from '@/store/moduleNames'
import CourseIntroduction from '@/components/bookcourse/CourseIntroduction.vue'
import ProgressLoader from '@/components/shared/ProgressLoader'
import CheckoutWindow from '@/components/checkout/CheckoutWindow.vue'
import BackButton from '@/components/shared/BackButton.vue'
import SelectUsers from '@/modules/subAccounts/components/SelectUsers.vue'

export default {
    components: {
        CourseIntroduction,
        ProgressLoader,
        CheckoutWindow,
        BackButton,
        SelectUsers,
    },
    data() {
        return {
            courseItem: {},
            error: '',
            courseBookable: false,
            reason: '',
            loading: true,
            loadingCourse: false,
            validationError: '',
            checkoutWindowKey: false,
            courseExists: false,
            selectedSubAccounts: [],
            bookingType: 'myself',
            registeredUsers: [],
            isRegisteredAsFamilyBooking: false,
            hasMainUserRegistered: false,
        }
    },
    watch: {
        $route: {
            handler(to, from) {
                if (to !== from) {
                    this.setRequiredDataWithLoading()
                }
            },
            immediate: true,
        },
        isUserAuth: {
            handler() {
                this.setRequiredData()
            },
        },
    },
    computed: {
        ...mapGetters({
            user: `${AUTHENTICATION_MODULE_NAME}/getUser`,
            isUserAuth: `${AUTHENTICATION_MODULE_NAME}/isUserAuth`,
            studio: `${STUDIO_MODULE_NAME}/getStudio`,
        }),
        normalMemberships() {
            return this.studio.memberships
        },
        isBookable() {
            return this.courseBookable && this.isUserAuth
        },
        countBooking() {
            return this.bookingType === 'myself'
                ? 1
                : this.selectedSubAccounts.length
        },
        countTotalPrice() {
            return this.courseItem.price * this.countBooking
        },
        isFamilyBooking() {
            return this.bookingType === 'family'
        },
        spotsAvailable() {
            if (
                this.courseItem.capacity === null ||
                this.courseItem.capacity === undefined
            ) {
                return null
            }

            return this.courseItem.capacity - this.courseItem.registrationCount
        },
    },
    methods: {
        async setRequiredDataWithLoading() {
            this.loading = true
            await this.setRequiredData()
            this.loading = false
        },
        async setRequiredData() {
            const promises = [this.setCourses()]
            await Promise.all(promises)
        },
        async setCourses() {
            this.loadingCourse = this.isUserAuth // to skip loading animation when user is not logged in
            const courseId = this.$route.params.courseId
            const result = await getCourseByCourseId(courseId)
            if (result.success) {
                this.courseItem = result.doc
                this.courseBookable = result.isBookable
                this.reason = result.reason

                this.registeredUsers = result.registeredUsers
                this.isRegisteredAsFamilyBooking = result.isFamilyBooking
                this.hasMainUserRegistered = result.hasMainUserRegistered

                if (this.isRegisteredAsFamilyBooking) {
                    this.bookingType = 'family'
                }

                this.courseExists = true
            } else {
                this.error = result.error
                this.courseExists = false
            }
            this.loadingCourse = false
        },
        submit() {
            this.$refs.CheckoutWindow.openDialog()
        },
        async reload() {
            await this.setCourses()
            this.toggleKeys()
        },
        toggleKeys() {
            this.checkoutWindowKey = !this.checkoutWindowKey
        },
        handleError(error) {
            this.validationError = error
        },
        onChangeBookingType(bookingType) {
            this.bookingType = bookingType
        },
        updateSelectedSubAccounts(selectedSubAccounts) {
            this.selectedSubAccounts = selectedSubAccounts
        },
    },
}
</script>

<style scoped>
.course-error {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-content: center;
    align-items: center;
}
</style>
